/**
 * React Query 클라이언트 및 Mutation 후 갱신 표준 (04-safety-standards: STALE_UI_PREVENTION)
 *
 * - Mutation 성공 시 invalidateAfterMutation(affectedKeys) 호출로 Stale UI 방지.
 * - useQuery 사용 시 동일한 queryKey를 사용하면 자동 refetch됨.
 *
 * @see doc/system-completion-plan/04-gaps/STALE_UI_PATTERN.md 표준 패턴·queryKeys 확장 가이드
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1분
      retry: 1,
    },
  },
});

// ============================================
// 🎯 Query Key 팩토리 (표준화)
// ============================================

export const queryKeys = {
  entityUserLink: (tenantId: string, entityId: string) =>
    ['entity-user-link', tenantId, entityId] as const,
  tenantInfo: (tenantId: string) => ['tenant-info', tenantId] as const,
  businessDomainList: (tenantId: string, domain: string) =>
    ['business-domain-list', tenantId, domain] as const,
  businessDomainDetail: (tenantId: string, domain: string, id: string) =>
    ['business-domain-detail', tenantId, domain, id] as const,
  schema: (tenantId: string, domain?: string) =>
    domain !== undefined && domain !== ''
      ? (['schema', tenantId, domain] as const)
      : (['schema', tenantId] as const),
  /** Finance voucher list useQuery — params 생략 시 prefix invalidate용 */
  voucherList: (tenantId: string, params?: Record<string, unknown>) =>
    params !== undefined
      ? (['voucher', 'list', tenantId, params] as const)
      : (['voucher', 'list', tenantId] as const),
};

/**
 * Mutation 성공 후 해당 리소스 쿼리 무효화 (Stale UI 방지).
 * useQuery와 같은 queryKey를 사용하는 곳이 자동 refetch됨.
 */
export function invalidateAfterMutation(
  keys: readonly [string, ...unknown[]] | string[]
): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: [...keys] });
}
